import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { useFormik } from "formik"
import * as Yup from "yup"

// components
import AppCard from '../../components/AppCard'
import AppForm from '../../components/form/form-template/AppForm'
import ActionButtonForm from '../../components/form/general-input-form/ActionButtonForm'
import AppModalConfirmation from 'src/components/modals/AppModalConfirmation'
import AppModalFilePreview from 'src/components/modals/AppModalFilePreview';

// helpers
import Axios from '../../helpers/axios'
import { singleTemplateMessageOptionsSelect, singleContactOptionSelect } from '../../helpers/format-options'
import useToastMessage from 'src/helpers/toast-message'
import { capitalizeFirstLetter } from 'src/helpers/text-format'

const SendTemplates = () => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { showSuccess, showError } = useToastMessage();
  const isLoading = useSelector((state) => state.isLoading)

  const [templateOption, setTemplateOption] = useState([])
  const [templateList, setTemplateList] = useState([])
  const [templateValue, setTemplateValue] = useState(null)
  const [templateHeaderComponents, setTemplateHeaderComponents] = useState([])

  const [recipientOption, setRecipientOption] = useState([])
  const [recipientValue, setRecipientValue] = useState(null)

  const [showModalConfirm, setShowModalConfirm] = useState({ show: false })
  const [showDeleteItem, setShowDeleteItem] = useState({ show: false, item: {} })

  const [previewModal, setPreviewModal] = useState({
    isOpen: false,
    type: null, // 'image', 'document', 'video'
    url: null,
    fileName: null
  });

  const getValidationSchema = () => {
    const baseSchema = {
      template_name: Yup.string().required('Template is required'),
      template_parameters: Yup.array().of(Yup.string().required('Parameters is required')),
      template_recipients: Yup.array().min(1, 'Recipients is required').required('Recipients is required'),
    };

    // Add file validation if header format is not TEXT
    if (templateHeaderComponents?.format && templateHeaderComponents.format !== 'TEXT') {
      baseSchema.file = Yup.array()
        .min(1, `File is required for ${capitalizeFirstLetter(templateHeaderComponents.format)} format`)
        .required(`File is required for ${capitalizeFirstLetter(templateHeaderComponents.format)} format`);
    }

    return Yup.object(baseSchema);
  };

  const formik = useFormik({
    initialValues: {
      template_name: '',
      template_language: '',
      template_parameters: [],
      template_message: '',
      file: [],
      template_recipients: []
    },
    validationSchema: getValidationSchema(),
    enableReinitialize: true,
    validateOnBlur: false,
    validateOnChange: false,
    onSubmit: async (values, { setSubmitting }) => {
      handleSave(values)
      setSubmitting(false)
    },
  });

  useEffect(() => {
    fetchTemplateMessage()
    fetchAllContacts()
  }, [])

  useEffect(() => {
    if (templateValue && templateList) {
      let template = templateList.filter(item => item.id == templateValue.value)[0]
      if (template) {
        const templateComponents = template?.components || []
        let componentHeader = templateComponents?.filter(e => e.type == 'HEADER')[0]
        setTemplateHeaderComponents(componentHeader)
        let componentBody = templateComponents?.filter(e => e.type == 'BODY')[0]
        let parameters = componentBody?.example?.body_text || []
        formik.setFieldValue('template_message', componentBody?.text)
        if (parameters?.length > 0) {
          for (let index in parameters) {
            formik.setFieldValue('template_parameters', parameters[index])
          }
        } else {
          formik.setFieldValue('template_parameters', [])
        }
      }
    }
  }, [templateList, templateValue])

  const fetchTemplateMessage = () => {
    dispatch({
      type: 'SET_IS_LOADING',
      isLoading: true
    })
    Axios({
      method: "get",
      url: "/api/whatsapp/v1/message/template/list",
    })
      .then(({ data }) => {
        if (data?.success) {
          setTemplateOption(singleTemplateMessageOptionsSelect(data?.value))
          setTemplateList(data?.value)
        }
      })
      .catch((err) => {
        console.error(err);
        const errMsg = err?.response?.data?.message
        showError(errMsg)
      })
      .finally(() => {
        dispatch({
          type: 'SET_IS_LOADING',
          isLoading: false
        })
      })
  }

  const fetchAllContacts = () => {
    Axios({
      method: "get",
      url: `/api/whatsapp/v1/contact`,
    })
      .then(({ data }) => {
        if (data) {
          setRecipientOption(singleContactOptionSelect(data?.value))
        }
      })
      .catch((err) => {
        console.error(err);
        const errMsg = err?.response?.data?.message
        showError(errMsg)
      });
  };

  const handleInputValue = (values) => {
    const formData = new FormData()
    formData.append('template_name', values?.template_name)
    formData.append('template_language', values?.template_language)
    formData.append('template_parameters', JSON.stringify(values?.template_parameters))
    formData.append('send_type', 'template')
    formData.append('message_type', 'text')

    if (values?.file?.length > 0) {
      formData.append('file', values?.file[0])
    }
    if (values?.template_recipients?.length > 0) {
      let recipients = values?.template_recipients?.map(item => item.value)
      formData.append('template_recipients', JSON.stringify(recipients))
    }

    return formData
  }

  const handlePost = (payload) => {
    dispatch({
      type: 'SET_IS_LOADING',
      isLoading: true
    })
    Axios({
      method: "post",
      url: `/api/whatsapp/v1/message/send`,
      data: payload
    })
      .then(({ data }) => {
        if (data?.status == 'success') {
          showSuccess('Success send template message')
          setTimeout(() => {
            window.location.reload()
          }, 1000)
        }
      })
      .catch((err) => {
        console.error(err);
        const errMsg = err?.response?.data?.message
        showError(errMsg)
      })
      .finally(() => {
        dispatch({
          type: 'SET_IS_LOADING',
          isLoading: false
        })
      })
  }

  const handleSave = (values) => {
    const inputPayload = handleInputValue(values)
    handlePost(inputPayload)
  }

  const handleSelectTemplate = (data) => {
    formik.setValues((values) => ({
      ...values,
      template_name: data ? data.label : '',
      template_language: data ? data.languange : ''
    }), false)

    setTemplateValue(data ? data : null)
  }

  const handleSelectRecipient = (data) => {
    formik.setValues((values) => ({
      ...values,
      template_recipients: data ? data : []
    }), false)

    setRecipientValue(data ? data : null)
  }

  const handleInputTemplateParam = (data, index) => {
    let text = data?.target?.value
    let params = formik.values.template_parameters
    params[index] = text
    formik.setFieldValue('template_parameters', params)
  }

  const handleFileInput = (values) => {
    const files = values
    if (files) {
      formik.setValues((prevValues) => ({
        ...prevValues,
        file: files
      }), false)
    }
  }

  const openPreview = (type, file) => {
    let fileUrl = URL.createObjectURL(file)
    
    setPreviewModal({
      isOpen: true,
      type,
      url: fileUrl,
      fileName: file?.name
    });
  };

  const closePreview = () => {
    setPreviewModal({
      isOpen: false,
      type: null,
      url: null,
      fileName: null
    });
  };

  const handleRemoveFile = () => {
    formik.setValues((prevValues) => ({
      ...prevValues,
      file: []
    }), false)

    setShowDeleteItem({ show: false, item: {} })
  }

  return (
    <>
      {
        previewModal.isOpen && (
          <AppModalFilePreview
            previewModal={previewModal}
            closePreview={closePreview}
          />
        )
      }
      {
        showDeleteItem.show && (
          <AppModalConfirmation 
            confirmationType='delete-item'
            itemName={showDeleteItem?.item?.attachment_original_name || showDeleteItem?.item?.name}
            itemDetails={showDeleteItem.item}
            itemType={showDeleteItem.type}
            itemIndex={showDeleteItem?.index}
            showModal={showDeleteItem}
            setShowModal={setShowDeleteItem}
            handleConfirmation={handleRemoveFile}
          />
        )
      }
      {
        showModalConfirm.show && (
          <AppModalConfirmation 
            confirmationType="confirmation"
            showModal={showModalConfirm}
            setShowModal={setShowModalConfirm}
            headerTitle="Confirmation"
            descriptionText="Are you sure want to send this message to all customers?"
            handleSubmit={formik.handleSubmit}
            disabled={formik.isSubmitting}
            isLoading={isLoading}
          />
        )
      }
      <AppCard 
        className='mb-4'
        headerTitle={<h4>Send Template Message</h4>}
        bodyContent={
          <AppForm 
            values={formik.values}
            handleChange={formik.handleChange}
            errors={formik.errors}
            templateOption={templateOption}
            handleSelectTemplate={handleSelectTemplate}
            templateValue={templateValue}
            handleInputTemplateParam={handleInputTemplateParam}
            templateHeaderComponents={templateHeaderComponents}
            handleFileInput={handleFileInput}
            openPreview={openPreview}
            handleConfirmDelete={setShowDeleteItem}
            recipientOption={recipientOption}
            recipientValue={recipientValue}
            handleSelectRecipient={handleSelectRecipient}
          />
        }
        footerContent={
          <ActionButtonForm
            isLoading={isLoading}
            textConfirm='Send'
            disabled={formik.isSubmitting || isLoading}
            onCancel={() => navigate('/template')}
            onSave={() => setShowModalConfirm({ show: true })}
          />
        }
      />
    </>
  )
}

export default SendTemplates